import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const lenisModulePromise = import('lenis');

/** Match Performance Lab feel for the open page. The top bound is handled below. */
const LENIS_LERP = 0.12;
const LENIS_WHEEL_MULTIPLIER = 0.9;
const LENIS_TOUCH_MULTIPLIER = 0.95;
/**
 * Lenis damps into a clamped target on a fixed clock, so the last stretch to
 * y=0 eases out the same way no matter how fast the wheel was. While an upward
 * gesture is still catching the top, raise lerp with that wheel speed. Slow
 * arrivals keep LENIS_LERP.
 */
const TOP_LERP_MAX = 0.36;
const UPWARD_SLOW_PX_PER_MS = 0.6;
const UPWARD_FAST_PX_PER_MS = 3.2;

type LenisAnimate = { lerp?: number };

let upwardPxPerMs = 0;
let upwardAt = 0;
let topApproachBoosted = false;
const GSAP_LAG_SMOOTHING_THRESHOLD = 500;
const GSAP_LAG_SMOOTHING_ADJUSTED_LAG = 33;

type LenisInstance = InstanceType<typeof import('lenis').default>;

let lenisInstance: LenisInstance | null = null;
let tickerCb: ((time: number) => void) | null = null;
let initPromise: Promise<void> | null = null;
let retainCount = 0;
/** Nested pause/resume (mega-menu freeze + modals). Lenis only runs at 0. */
let pauseCount = 0;

const LENIS_DESKTOP_QUERY = '(min-width: 992px)';
const FINE_POINTER_QUERY = '(pointer: fine)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Lenis on desktop fine-pointer only — native scroll on phones. */
export function shouldUseHomepageLenis(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return false;
  if (!window.matchMedia(LENIS_DESKTOP_QUERY).matches) return false;
  if (!window.matchMedia(FINE_POINTER_QUERY).matches) return false;
  return true;
}

function noteUpwardWheel(deltaY: number): void {
  if (deltaY >= 0) return;
  const now = performance.now();
  const dt = Math.max(8, upwardAt ? now - upwardAt : 16);
  const instant = Math.abs(deltaY) / dt;
  upwardPxPerMs = Math.max(instant, upwardPxPerMs * 0.82);
  upwardAt = now;
}

function tuneTopApproach(instance: LenisInstance): void {
  const animate = (instance as unknown as { animate?: LenisAnimate }).animate;
  if (!animate) return;

  const animated = instance.animatedScroll;
  const target = instance.targetScroll;
  const catchingTop =
    target <= 1 && animated > 1 && instance.direction <= 0;

  if (!catchingTop) {
    if (topApproachBoosted) {
      animate.lerp = LENIS_LERP;
      topApproachBoosted = false;
    }
    if (animated <= 1 || instance.direction > 0) upwardPxPerMs = 0;
    return;
  }

  const t = Math.min(
    1,
    Math.max(
      0,
      (upwardPxPerMs - UPWARD_SLOW_PX_PER_MS) /
        (UPWARD_FAST_PX_PER_MS - UPWARD_SLOW_PX_PER_MS),
    ),
  );
  const lerp = LENIS_LERP + t * (TOP_LERP_MAX - LENIS_LERP);
  animate.lerp = lerp;
  topApproachBoosted = lerp > LENIS_LERP;
}

function scheduleScrollTriggerRefresh(): void {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
  });
}

function wireScrollerProxy(instance: LenisInstance): void {
  ScrollTrigger.scrollerProxy(document.documentElement, {
    scrollTop(value) {
      if (arguments.length && value !== undefined) {
        instance.scrollTo(value, { immediate: true });
      }
      return instance.scroll;
    },
    getBoundingClientRect() {
      return {
        top: 0,
        left: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      };
    },
    pinType: document.body.style.transform ? 'transform' : 'fixed',
  });
}

export function getHomepageLenis(): LenisInstance | null {
  return lenisInstance;
}

/** Pause smooth scroll while a homepage modal / menu freeze is active. */
export function pauseHomepageLenis(): void {
  pauseCount += 1;
  if (pauseCount === 1) {
    lenisInstance?.stop();
  }
}

export function resumeHomepageLenis(): void {
  if (pauseCount === 0) return;
  pauseCount -= 1;
  if (pauseCount === 0) {
    lenisInstance?.start();
  }
}

export function destroyHomepageLenis(): void {
  initPromise = null;

  if (tickerCb) {
    gsap.ticker.remove(tickerCb);
    tickerCb = null;
  }

  gsap.ticker.lagSmoothing(
    GSAP_LAG_SMOOTHING_THRESHOLD,
    GSAP_LAG_SMOOTHING_ADJUSTED_LAG,
  );

  if (lenisInstance) {
    lenisInstance.destroy();
    lenisInstance = null;
  }
  upwardPxPerMs = 0;
  upwardAt = 0;
  topApproachBoosted = false;

  ScrollTrigger.scrollerProxy(document.documentElement, {});
  ScrollTrigger.refresh();
}

export function initHomepageLenis(): Promise<void> | null {
  if (!shouldUseHomepageLenis()) return null;
  if (lenisInstance) return initPromise;

  if (!initPromise) {
    initPromise = lenisModulePromise.then(({ default: Lenis }) => {
      if (lenisInstance || !shouldUseHomepageLenis()) return;

      const instance = new Lenis({
        autoRaf: false,
        lerp: LENIS_LERP,
        wheelMultiplier: LENIS_WHEEL_MULTIPLIER,
        touchMultiplier: LENIS_TOUCH_MULTIPLIER,
        virtualScroll(data) {
          noteUpwardWheel(data.deltaY);
          return true;
        },
      });

      lenisInstance = instance;
      wireScrollerProxy(instance);

      // Honor pauses acquired before Lenis finished lazy-loading (e.g. menu
      // opened on first paint of `/`).
      if (pauseCount > 0) {
        instance.stop();
      }

      instance.on('scroll', () => {
        ScrollTrigger.update();
      });

      tickerCb = (time: number) => {
        tuneTopApproach(instance);
        instance.raf(time * 1000);
      };
      gsap.ticker.add(tickerCb);
      gsap.ticker.lagSmoothing(
        GSAP_LAG_SMOOTHING_THRESHOLD,
        GSAP_LAG_SMOOTHING_ADJUSTED_LAG,
      );

      scheduleScrollTriggerRefresh();
    });
  }

  return initPromise;
}

export function retainHomepageLenis(): void {
  retainCount += 1;
  void initHomepageLenis();
}

export function releaseHomepageLenis(): void {
  retainCount = Math.max(0, retainCount - 1);
  if (retainCount === 0) {
    destroyHomepageLenis();
  }
}
